import React, { useMemo } from "react";

import { ClearableFilterAutocomplete } from "@components";

import type { PermissionsSource } from "../../modules/app/permissions/data/types";

// Keying off a Record forces exhaustiveness: adding a new PermissionsSource
// without a label here is a compile error. Literal order = dropdown order.
const SOURCE_LABELS: Record<PermissionsSource, string> = {
  "": "All sources",
  team: "Teams",
  role: "Roles",
  direct: "Direct grants",
};

type SourceOption = { value: PermissionsSource; label: string };

const SOURCE_OPTIONS: SourceOption[] = (
  Object.entries(SOURCE_LABELS) as Array<[PermissionsSource, string]>
).map(([value, label]) => ({ value, label }));

export interface SourceSelectorProps {
  value: PermissionsSource;
  onChange: (value: PermissionsSource) => void;
}

function SourceSelectorInner({ value, onChange }: SourceSelectorProps) {
  const selectedOption = useMemo(
    () => SOURCE_OPTIONS.find((option) => option.value === value),
    [value],
  );

  return (
    <ClearableFilterAutocomplete<SourceOption>
      label="Source"
      options={SOURCE_OPTIONS}
      value={selectedOption}
      active={!!value}
      // disableClearable guarantees a non-null option; default to "All sources".
      onChange={(option) => onChange(option?.value ?? "")}
      onClear={() => onChange("")}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) =>
        option.value === selected.value
      }
    />
  );
}

export const SourceSelector = React.memo(SourceSelectorInner);
